import React, { useState, useEffect, useMemo, useCallback, useContext, useRef } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import {
  tripsAPI,
  trucksAPI,
  operationsAPI,
  driversAPI,
  companiesAPI,
  transportBoardAPI,
  printingAPI,
} from '../lib/api';
import { lotsAPI } from '../lib/lotsApi';
import { fetchAllPages } from '../lib/fetchAllPages';
import {
  Plus,
  ArrowRight,
  Clock,
  Map,
  Search,
  X,
  LayoutGrid,
  KanbanSquare,
  List,
  CalendarRange,
  Boxes,
  SlidersHorizontal,
  Send,
  ParkingSquare,
  LogOut,
  Loader2,
} from 'lucide-react';
import { BarrePagination } from '../components/ui/pagination-bar';
import { usePaginationServeur } from '../hooks/usePagination';
import { useDerniereDemande } from '../hooks/useDerniereDemande';
import { useDebouncedValue } from '../hooks/use-debounced-value';
import { useEstMobile } from '../hooks/useEstMobile';
import TripsKanbanBoard from '../components/partials/TripsKanbanBoard';
import { PrintMenu } from '../components/business/PrintMenu';
import { Card, CardContent } from '../components/ui/card';
import { ViewSwitcher } from '../components/ui/view-switcher';
import { PageHeader } from '../components/ui/page-header';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { SearchableSelect } from '../components/ui/searchable-select';
import {
  cn,
  getStatusBadgeClass,
  formatDate,
  formatRelativeTime,
  getTripProgress,
  getTripPhase,
  isCustomsStatus,
  phaseLabel,
} from '../lib/utils';
import { toast } from '../components/ui/sonner';
import { libelle, nomLisible, texteSur } from '../lib/libelles';
import { messageErreur } from '../lib/erreurs';
import {
  GROUPES_ETAPE,
  avecFiltres,
  lireFiltresTrajets,
  nombreDates,
  nombreFiltresActifs,
  parametresServeur,
  sansFiltres,
} from '../lib/filtresTrajets';
import { AuthContext } from '../context/AuthContext';
import { LoadingState } from '../components/ui/loading-state';
import { EmptyState } from '../components/ui/empty-state';
import { useRafraichirSur } from '../hooks/useRafraichirSur';

const nouvelleCle = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `trajets-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const enParking = (trip) => Boolean(trip?.parked_at) && !trip?.parking_left_at;

const TOUS = 'tous';

/**
 * Le lot d'un trajet de lot, à la place de l'itinéraire: une pastille avec la
 * référence du lot, qui mène à lui. Sans référence connue (module des lots
 * absent, lot introuvable), la pastille dit seulement « Lot attribué ».
 */
const PastilleLot = ({ lotId, reference }) => {
  const gabarit =
    'inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 px-2 py-0.5 text-xs font-semibold';
  if (!reference) {
    return (
      <span className={cn(gabarit, 'text-muted-foreground')} data-testid="trip-lot-pastille">
        <Boxes className="h-3.5 w-3.5" aria-hidden="true" />
        Lot attribué
      </span>
    );
  }
  return (
    <Link
      to={`/lots/${lotId}`}
      onClick={(event) => event.stopPropagation()}
      className={cn(gabarit, 'font-mono text-primary underline-offset-2 hover:underline')}
      aria-label={`Lot ${reference}`}
      data-testid="trip-lot-pastille"
    >
      <Boxes className="h-3.5 w-3.5" aria-hidden="true" />
      {reference}
    </Link>
  );
};


const TripsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState('table');
  const [trips, setTrips] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [operations, setOperations] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [depots, setDepots] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  // Le squelette ne remplace la page qu'au premier chargement: un filtre
  // choisi ensuite laisse la liste (et le volet de filtres) en place.
  const [dejaCharge, setDejaCharge] = useState(false);
  const [referencesLots, setReferencesLots] = useState({});
  const [filtresOuverts, setFiltresOuverts] = useState(false);
  const mobile = useEstMobile();
  const [totalTrips, setTotalTrips] = useState(0);
  const [customsCount, setCustomsCount] = useState(0);
  const [kanbanColumns, setKanbanColumns] = useState(null);
  const [isKanbanLoading, setIsKanbanLoading] = useState(false);
  const [coches, setCoches] = useState(() => new Set());
  const [actionEnCours, setActionEnCours] = useState(null);
  const [envoiEnCours, setEnvoiEnCours] = useState(false);
  const cleEnvoi = useRef(null);

  // Hors du fournisseur d'authentification (tests), l'écran reste utilisable.
  const peutEcrire = !auth
    || (typeof auth.canWriteModule === 'function' && auth.canWriteModule('trips'))
    || auth.user?.role === 'LOGISTIQUE';
  // La référence d'un lot ne se lit qu'avec le module des lots.
  const peutLireLots = typeof auth?.hasModule === 'function' && auth.hasModule('lots');

  // Les filtres vivent dans l'adresse: un lien partagé rouvre la même liste.
  const filtres = useMemo(() => lireFiltresTrajets(searchParams), [searchParams]);
  const cleFiltres = JSON.stringify(parametresServeur(filtres));
  const parametres = useMemo(() => JSON.parse(cleFiltres), [cleFiltres]);
  const filtresActifs = nombreFiltresActifs(filtres);

  const [searchQuery, setSearchQuery] = useState(() => filtres.q);
  const rechercheDifferee = useDebouncedValue(searchQuery);

  const changerFiltres = useCallback(
    (changements) => setSearchParams((precedent) => avecFiltres(precedent, changements), { replace: true }),
    [setSearchParams],
  );

  // La recherche part au serveur une fois la frappe posée.
  useEffect(() => {
    if (rechercheDifferee.trim() === filtres.q) return;
    changerFiltres({ q: rechercheDifferee.trim() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rechercheDifferee]);

  // Pagination commune: page et taille dans l'adresse, barre sous la liste.
  // Un autre filtre ramène à la première page.
  const pagination = usePaginationServeur(totalTrips, { reinitialiser: cleFiltres });
  const { page, limit: pageSize } = pagination;

  // Les listes de choix (camions, convois, chauffeurs, transporteurs, dépôts)
  // ne dépendent pas des filtres: chargées une fois, entières.
  const loadReferences = useCallback(async () => {
    const [trucksPage, operationsPage, driverItems, companyItems, depotItems] = await Promise.all([
      fetchAllPages((p) => trucksAPI.getAllPaged(p)).catch(() => ({ items: [] })),
      fetchAllPages((p) => operationsAPI.getAllPaged(p)).catch(() => ({ items: [] })),
      // Les noms de chauffeurs sont un confort de lecture: s'ils manquent, la
      // liste des trajets s'affiche quand même.
      fetchAllPages((p) => driversAPI.getAllPaged(p))
        .then((result) => result.items || [])
        .catch(() => []),
      Promise.resolve()
        .then(() => companiesAPI.getAll())
        .then((result) => (Array.isArray(result) ? result : result?.items || result?.data || []))
        .catch(() => []),
      Promise.resolve()
        .then(() => tripsAPI.getLoadingDepots())
        .then((result) => (Array.isArray(result) ? result : []))
        .catch(() => []),
    ]);
    setTrucks(trucksPage.items || []);
    setOperations(operationsPage.items || []);
    setDrivers(driverItems);
    setCompanies(companyItems);
    setDepots(depotItems);
  }, []);

  // Chaque filtre, page ou retour relance la lecture: seule la dernière demande
  // a le droit d'écrire (voir `useDerniereDemande`).
  const nouvelleDemande = useDerniereDemande();

  const loadData = useCallback(async ({ silencieux = false } = {}) => {
    const perimee = nouvelleDemande();
    try {
      if (!silencieux) setIsLoading(true);
      const [douane, tripsPage] = await Promise.all([
        // Le compteur « à la douane » porte sur toute l'exploitation, pas sur
        // la page affichée.
        tripsAPI
          .getAllPaged({ status_group: 'customs', page: 1, page_size: 1 })
          .then((result) => Number(result?.total) || 0)
          .catch(() => 0),
        tripsAPI.getAllPaged({ ...parametres, page, page_size: pageSize }),
      ]);
      if (perimee()) return;
      const tripItems = tripsPage.items || [];
      setTrips(tripItems);
      setTotalTrips(tripsPage.total ?? tripItems.length);
      setCustomsCount(douane);
    } catch (err) {
      if (perimee()) return;
      console.error('Failed to load trips:', err);
      toast.error(messageErreur(err, 'Impossible de charger les trajets'));
    } finally {
      if (!perimee()) {
        setIsLoading(false);
        setDejaCharge(true);
      }
    }
  }, [page, pageSize, parametres, nouvelleDemande]);

  // Le kanban lit les colonnes du moteur de workflow serveur, qui voit tous
  // les trajets: une colonne comptée sur la page courante n'est pas un état de
  // l'exploitation.
  const loadKanban = useCallback(async ({ silencieux = false } = {}) => {
    if (viewMode !== 'kanban') return;
    if (!silencieux) setIsKanbanLoading(true);
    try {
      setKanbanColumns(await transportBoardAPI.getKanban());
    } catch (err) {
      console.error('Failed to load transport kanban:', err);
      setKanbanColumns(null);
    } finally {
      setIsKanbanLoading(false);
    }
  }, [viewMode]);

  useEffect(() => {
    loadKanban();
  }, [loadKanban]);

  useEffect(() => {
    loadReferences();
  }, [loadReferences]);

  // Un trajet de lot n'a ni départ ni destination: la liste montre à la place la
  // référence de son lot. Les lots se lisent une fois, dès qu'une ligne en porte un.
  useEffect(() => {
    const inconnus = trips.some((trip) => trip.lot_id && referencesLots[trip.lot_id] === undefined);
    if (!peutLireLots || !inconnus) return undefined;
    let annule = false;
    lotsAPI
      .list()
      .catch(() => [])
      .then((lots) => {
        if (annule) return;
        const lues = Object.fromEntries((lots || []).map((lot) => [lot.id, lot.reference || null]));
        setReferencesLots((avant) => {
          const suite = { ...avant, ...lues };
          trips.forEach((trip) => {
            if (trip.lot_id && suite[trip.lot_id] === undefined) suite[trip.lot_id] = null;
          });
          return suite;
        });
      });
    return () => {
      annule = true;
    };
  }, [trips, referencesLots, peutLireLots]);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadData, location]);

  // Une autre page ou d'autres filtres: les cases cochées ne valent plus.
  useEffect(() => {
    setCoches(new Set());
  }, [cleFiltres, page, pageSize]);

  // Une écriture faite ailleurs (autre écran, autre onglet, file hors ligne rejouée) recharge ces chiffres: jamais de compteur figé.
  useRafraichirSur(['trajets', 'stock', 'commandes', 'convois', 'repartition', 'depenses'], (options) =>
    Promise.all([loadData(options), loadKanban(options), loadReferences()]),
  );

  const getTruck = (truckId) => trucks.find((t) => t.id === truckId);
  const getDriver = (driverId) => drivers.find((d) => d.id === driverId);

  const operationFilterOptions = useMemo(
    () => [
      { value: 'all', label: 'Tous les convois' },
      ...operations.map((op) => ({
        value: op.id,
        label: nomLisible(op.code, 'Convoi sans nom'),
        description: op.route || undefined,
      })),
    ],
    [operations]
  );

  const transporteurOptions = useMemo(
    () => [
      { value: '', label: 'Tous les transporteurs' },
      ...companies
        .filter((company) => company?.id)
        .map((company) => ({ value: company.id, label: nomLisible(company.name, 'Société sans nom') })),
    ],
    [companies],
  );

  const depotOptions = useMemo(() => {
    const valeurs = new Set(depots);
    if (filtres.depot) valeurs.add(filtres.depot);
    return [
      { value: '', label: 'Tous les dépôts' },
      ...[...valeurs].sort().map((depot) => ({ value: depot, label: depot })),
    ];
  }, [depots, filtres.depot]);

  // Le document imprimé doit dire ce que l'écran affiche: il reçoit exactement
  // les filtres de la liste (jamais la pagination). Un tirage qui ignore les
  // filtres est pire qu'aucun tirage — on ne s'aperçoit pas qu'il ment.
  const printItems = [
    {
      label: 'Liste des trajets',
      filename: 'liste-des-trajets',
      url: printingAPI.trips,
      formats: ['pdf', 'xlsx', 'csv'],
      params: parametres,
    },
    // La feuille de convoi n'a de sens qu'avec un convoi choisi.
    ...(filtres.convoi !== 'all'
      ? [
          {
            label: 'Feuille de convoi',
            filename: 'feuille-de-convoi',
            url: printingAPI.convoy(filtres.convoi),
            formats: ['pdf', 'xlsx'],
          },
        ]
      : []),
  ];

  const envoyerAuResponsable = async () => {
    if (envoiEnCours) return;
    // Même clé tant que l'envoi n'a pas abouti: un nouvel essai après une
    // coupure ne part pas deux fois.
    if (!cleEnvoi.current) cleEnvoi.current = nouvelleCle();
    setEnvoiEnCours(true);
    try {
      const resultat = await printingAPI.sendTrips(parametres, cleEnvoi.current);
      cleEnvoi.current = null;
      const nombre = Number(resultat?.recipients) || 0;
      toast.success(
        nombre > 1 ? `Liste envoyée à ${nombre} responsables` : 'Liste envoyée au responsable',
      );
    } catch (err) {
      toast.error(messageErreur(err, "La liste n'a pas pu partir"));
    } finally {
      setEnvoiEnCours(false);
    }
  };

  const basculerCoche = (tripId) =>
    setCoches((avant) => {
      const apres = new Set(avant);
      if (apres.has(tripId)) apres.delete(tripId);
      else apres.add(tripId);
      return apres;
    });

  const toutCocher = (valeur) =>
    setCoches(valeur ? new Set(trips.map((trip) => trip.id)) : new Set());

  const toutEstCoche = trips.length > 0 && trips.every((trip) => coches.has(trip.id));

  const agirSurParking = async (action) => {
    if (actionEnCours || coches.size === 0) return;
    setActionEnCours(action);
    try {
      const resultat = await tripsAPI.setParking({
        tripIds: [...coches],
        action,
        idempotencyKey: nouvelleCle(),
      });
      const faits = (resultat?.changed || []).length;
      const refuses = (resultat?.refused || []).length;
      const verbe = action === 'ENTER' ? 'mis en parking' : 'sortis du parking';
      if (faits > 0) toast.success(`${faits} trajet${faits > 1 ? 's' : ''} ${verbe}`);
      else if (refuses === 0) toast.success('Rien à changer');
      if (refuses > 0) {
        toast.error(
          refuses > 1
            ? `${refuses} trajets livrés, clôturés ou annulés n'ont pas été mis en parking`
            : "1 trajet livré, clôturé ou annulé n'a pas été mis en parking",
        );
      }
      setCoches(new Set());
      await Promise.all([loadData({ silencieux: true }), loadReferences()]);
    } catch (err) {
      toast.error(messageErreur(err, "Le parking n'a pas pu être mis à jour"));
    } finally {
      setActionEnCours(null);
    }
  };

  const effacerFiltres = () => {
    setSearchQuery('');
    setSearchParams((precedent) => sansFiltres(precedent), { replace: true });
  };

  const datesPosees = nombreDates(filtres);

  const largeur = (classe) => (mobile ? 'w-full' : classe);
  const filtresSecondairesActifs = [
    filtres.convoi && filtres.convoi !== 'all',
    filtres.transporteur,
    filtres.depot,
    filtres.parking,
    filtres.docSortie,
    datesPosees > 0,
  ].filter(Boolean).length;

  const filtresSecondaires = (
    <>
      <SearchableSelect
        className={cn('input-base', largeur('w-44'))}
        data-testid="operation-filter"
        aria-label="Convoi"
        placeholder="Convoi"
        searchPlaceholder="Rechercher un convoi…"
        emptyMessage="Aucun convoi trouvé"
        value={filtres.convoi || 'all'}
        onValueChange={(v) => changerFiltres({ convoi: v })}
        options={operationFilterOptions}
      />
      <SearchableSelect
        className={cn('input-base', largeur('w-52'))}
        data-testid="transporter-filter"
        aria-label="Transporteur"
        placeholder="Transporteur"
        searchPlaceholder="Rechercher un transporteur…"
        emptyMessage="Aucun transporteur trouvé"
        value={filtres.transporteur}
        onValueChange={(v) => changerFiltres({ transporteur: v })}
        options={transporteurOptions}
      />
      <SearchableSelect
        className={cn('input-base', largeur('w-40'))}
        data-testid="depot-filter"
        aria-label="Dépôt"
        placeholder="Dépôt"
        searchPlaceholder="Rechercher un dépôt…"
        emptyMessage="Aucun dépôt trouvé"
        value={filtres.depot}
        onValueChange={(v) => changerFiltres({ depot: v })}
        options={depotOptions}
      />
      <Select value={filtres.parking || TOUS} onValueChange={(v) => changerFiltres({ parking: v === TOUS ? '' : v })}>
        <SelectTrigger className={cn('input-base', largeur('w-36'))} data-testid="parking-filter" aria-label="Parking">
          <SelectValue placeholder="Parking" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={TOUS}>Parking : tous</SelectItem>
          <SelectItem value="oui">{libelle('parking_etat', 'oui')}</SelectItem>
          <SelectItem value="non">{libelle('parking_etat', 'non')}</SelectItem>
        </SelectContent>
      </Select>
      <Select value={filtres.docSortie || TOUS} onValueChange={(v) => changerFiltres({ docSortie: v === TOUS ? '' : v })}>
        <SelectTrigger className={cn('input-base', largeur('w-60'))} data-testid="exit-document-filter" aria-label="Document de sortie">
          <SelectValue placeholder="Document de sortie" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={TOUS}>Document de sortie : tous</SelectItem>
          <SelectItem value="oui">{libelle('document_sortie', 'oui')}</SelectItem>
          <SelectItem value="non">{libelle('document_sortie', 'non')}</SelectItem>
        </SelectContent>
      </Select>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className={cn('gap-2', mobile && 'w-full justify-start')} data-testid="dates-filter">
            <CalendarRange className="h-4 w-4" aria-hidden="true" />
            Dates
            {datesPosees > 0 ? (
              <Badge variant="secondary" className="ml-1 px-1.5 py-0 text-xs">{datesPosees}</Badge>
            ) : null}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-72 max-w-[calc(100vw-2rem)] space-y-3">
          {[
            { titre: 'Chargement', du: 'chargeDu', au: 'chargeAu' },
            { titre: 'Sortie douane', du: 'douaneDu', au: 'douaneAu' },
          ].map((periode) => (
            <fieldset key={periode.titre} className="space-y-1.5">
              <legend className="text-sm font-medium">{periode.titre}</legend>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label htmlFor={`trips-${periode.du}`} className="text-xs text-muted-foreground">Du</Label>
                  <Input
                    id={`trips-${periode.du}`}
                    type="date"
                    value={filtres[periode.du]}
                    onChange={(e) => changerFiltres({ [periode.du]: e.target.value })}
                    className="input-base text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor={`trips-${periode.au}`} className="text-xs text-muted-foreground">Au</Label>
                  <Input
                    id={`trips-${periode.au}`}
                    type="date"
                    value={filtres[periode.au]}
                    onChange={(e) => changerFiltres({ [periode.au]: e.target.value })}
                    className="input-base text-sm"
                  />
                </div>
              </div>
            </fieldset>
          ))}
        </PopoverContent>
      </Popover>
    </>
  );

  if (isLoading && !dejaCharge) {
    return (
      <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8">
        <LoadingState shape="cards" />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8" data-testid="trips-page">
      <PageHeader
        eyebrow="Transport"
        title="Trajets"
        description={`${totalTrips || trips.length} trajets ${filtresActifs ? 'dans cette sélection' : 'au total'}`}
        alertesSousTitre
        alertes={
          customsCount > 0
            ? [
                {
                  niveau: 'attention',
                  nombre: customsCount,
                  court: 'à la douane',
                  texte:
                    customsCount === 1
                      ? '1 camion est à la douane'
                      : `${customsCount} camions sont à la douane`,
                  action: {
                    label: 'Voir',
                    onClick: () => changerFiltres({ etape: 'customs' }),
                  },
                },
              ]
            : []
        }
        actions={
          <>
            <ViewSwitcher
              value={viewMode}
              onChange={setViewMode}
              options={[
                { value: 'table', titre: 'Tableau', texteLecteur: 'Afficher en tableau', icone: List, testid: 'trips-view-table' },
                { value: 'grid', titre: 'Cartes', texteLecteur: 'Afficher en cartes', icone: LayoutGrid, testid: 'trips-view-grid' },
                { value: 'kanban', titre: 'Colonnes par étape', texteLecteur: 'Afficher en colonnes par étape', icone: KanbanSquare, testid: 'trips-view-kanban' },
              ]}
            />
            <PrintMenu items={printItems} label="Imprimer" />
            {peutEcrire ? (
              <Button
                variant="outline"
                size="sm"
                onClick={envoyerAuResponsable}
                disabled={envoiEnCours}
                data-testid="trips-send-manager"
              >
                {envoiEnCours ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <Send className="h-4 w-4" aria-hidden="true" />
                )}
                Envoyer au responsable
              </Button>
            ) : null}
            <Button
              onClick={() => navigate('/trips/new')}
              data-testid="new-trip-btn"
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              Nouveau trajet
            </Button>
          </>
        }
      />

      {/* Recherche étroite, filtres à sa droite. Sur téléphone, seuls la recherche et l'étape restent
          en vue: les autres filtres s'ouvrent derrière « Filtres ». */}
      <div className="flex flex-wrap items-center gap-2" data-testid="trips-filters">
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/70" aria-hidden="true" />
          <Input
            type="text"
            placeholder="Plaque, N° de bon, chauffeur…"
            aria-label="Rechercher un trajet"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input-base pl-9 pr-9 text-sm"
            data-testid="trips-search"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              aria-label="Effacer la recherche"
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-muted rounded"
            >
              <X className="w-4 h-4 text-muted-foreground/70" aria-hidden="true" />
            </button>
          )}
        </div>
        <Select value={filtres.etape} onValueChange={(v) => changerFiltres({ etape: v })}>
          <SelectTrigger className={cn('input-base', mobile ? 'min-w-0 flex-1' : 'w-44')} data-testid="status-filter" aria-label="Étape">
            <SelectValue placeholder="Étape" />
          </SelectTrigger>
          <SelectContent>
            {GROUPES_ETAPE.map((code) => (
              <SelectItem key={code} value={code}>
                {libelle('filtre_liste_trajets', code)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {mobile ? (
          <Sheet open={filtresOuverts} onOpenChange={setFiltresOuverts}>
            <SheetTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2" data-testid="trips-filters-open">
                <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
                Filtres
                {filtresSecondairesActifs > 0 ? (
                  <Badge variant="secondary" className="ml-1 px-1.5 py-0 text-xs">{filtresSecondairesActifs}</Badge>
                ) : null}
              </Button>
            </SheetTrigger>
            <SheetContent side="bottom" aria-describedby={undefined} className="max-h-[85vh] overflow-y-auto border-border bg-card">
              <SheetHeader>
                <SheetTitle className="text-left">Filtres</SheetTitle>
              </SheetHeader>
              <div className="mt-4 flex flex-col gap-2 pb-[env(safe-area-inset-bottom)]" data-testid="trips-filters-sheet">
                {filtresSecondaires}
              </div>
            </SheetContent>
          </Sheet>
        ) : (
          filtresSecondaires
        )}
        {filtresActifs > 0 ? (
          <Button variant="ghost" size="sm" onClick={effacerFiltres} data-testid="trips-clear-filters">
            <X className="h-4 w-4" aria-hidden="true" />
            Effacer les filtres
          </Button>
        ) : null}
      </div>

      {viewMode === 'table' && peutEcrire && coches.size > 0 ? (
        <div
          className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2"
          data-testid="trips-selection-bar"
        >
          <span className="text-sm font-medium">
            {coches.size} trajet{coches.size > 1 ? 's' : ''} coché{coches.size > 1 ? 's' : ''}
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={() => agirSurParking('ENTER')}
            disabled={Boolean(actionEnCours)}
            data-testid="trips-park-enter"
          >
            {actionEnCours === 'ENTER' ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <ParkingSquare className="h-4 w-4" aria-hidden="true" />
            )}
            {libelle('parking_action', 'ENTER')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => agirSurParking('LEAVE')}
            disabled={Boolean(actionEnCours)}
            data-testid="trips-park-leave"
          >
            {actionEnCours === 'LEAVE' ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <LogOut className="h-4 w-4" aria-hidden="true" />
            )}
            {libelle('parking_action', 'LEAVE')}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => toutCocher(false)}>
            Tout décocher
          </Button>
        </div>
      ) : null}

      {/* Trips List */}
      {viewMode === 'kanban' ? (
        isKanbanLoading ? (
          <LoadingState shape="cards" />
        ) : (
          <TripsKanbanBoard
            trips={trips}
            columns={kanbanColumns}
            getTruck={getTruck}
            getDriver={getDriver}
          />
        )
      ) : trips.length === 0 ? (
        <Card className="card-base">
          <CardContent className="p-0">
            <EmptyState
              variant={filtresActifs ? 'filtered' : 'empty'}
              icon={Map}
              title={filtresActifs ? 'Aucun résultat' : 'Aucun trajet enregistré'}
              description={
                filtresActifs
                  ? 'Aucun trajet ne correspond aux filtres actifs.'
                  : 'Un trajet suit un camion du chargement à la livraison : douane, incidents, volumes chargés et livrés, puis la facture.'
              }
              actionLabel={filtresActifs ? undefined : 'Créer un trajet'}
              onAction={() => navigate('/trips/new')}
              className="border-0"
            />
          </CardContent>
        </Card>
      ) : viewMode === 'table' ? (
        <Card className={cn('card-base transition-opacity', isLoading && 'opacity-60')} aria-busy={isLoading}>
          <CardContent className="p-0">
            <Table compact>
              <TableHeader>
                <TableRow>
                  {peutEcrire ? (
                    <TableHead className="w-10">
                      <Checkbox
                        checked={toutEstCoche}
                        onCheckedChange={(valeur) => toutCocher(Boolean(valeur))}
                        aria-label="Cocher tous les trajets de la page"
                        data-testid="trips-check-all"
                      />
                    </TableHead>
                  ) : null}
                  <TableHead>Camion</TableHead>
                  <TableHead>Chauffeur</TableHead>
                  <TableHead>Itinéraire</TableHead>
                  <TableHead>Chargement</TableHead>
                  <TableHead>Étape</TableHead>
                  <TableHead className="text-right">Avancement</TableHead>
                  <TableHead>Mis à jour</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {trips.map((trip) => {
                  const truck = getTruck(trip.truck_id);
                  const driver = getDriver(trip.driver_id);
                  return (
                    <TableRow
                      key={trip.id}
                      className="cursor-pointer"
                      onClick={() => navigate(`/trips/${trip.id}`)}
                      data-testid={`trip-row-${trip.id}`}
                      data-state={coches.has(trip.id) ? 'selected' : undefined}
                    >
                      {peutEcrire ? (
                        <TableCell className="w-10" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={coches.has(trip.id)}
                            onCheckedChange={() => basculerCoche(trip.id)}
                            aria-label={`Cocher le trajet ${nomLisible(trip.tractor_plate, truck?.matricule, 'du camion')}`}
                            data-testid={`trip-check-${trip.id}`}
                          />
                        </TableCell>
                      ) : null}
                      <TableCell className="whitespace-nowrap font-mono font-semibold">
                        {nomLisible(trip.tractor_plate, truck?.matricule, 'Camion non précisé')}
                      </TableCell>
                      <TableCell>
                        {driver?.full_name || driver?.phone || (trip.driver_id ? 'Chauffeur assigné' : 'Non assigné')}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {trip.lot_id && !trip.departure_country && !trip.destination_country ? (
                          <PastilleLot lotId={trip.lot_id} reference={referencesLots[trip.lot_id]} />
                        ) : (
                          <>
                            {texteSur(trip.departure_country, 'Départ non précisé')} →{' '}
                            {texteSur(trip.destination_country, 'Destination non précisée')}
                          </>
                        )}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-sm">
                        <span>{trip.loading_depot || '—'}</span>
                        {trip.loading_date ? (
                          <span className="ml-1 text-muted-foreground">· {formatDate(trip.loading_date)}</span>
                        ) : null}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <Badge
                          className={cn(
                            'status-badge text-xs',
                            getStatusBadgeClass(trip.status),
                          )}
                        >
                          {libelle('tripStatus', trip.status)}
                        </Badge>
                        {enParking(trip) && (
                          <Badge variant="outline" className="ml-1 text-xs" data-testid={`trip-parked-${trip.id}`}>
                            {libelle('parking_etat', 'oui')}
                          </Badge>
                        )}
                        {trip.is_blocked && (
                          <Badge className="ml-1 bg-destructive text-white text-xs">
                            Bloqué
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {getTripProgress(trip.status)} %
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-muted-foreground">
                        {formatRelativeTime(trip.updated_at)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <div className="go-md2-lg3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {trips.map((trip) => {
            const truck = getTruck(trip.truck_id);
            const progress = getTripProgress(trip.status);
            const atCustoms = isCustomsStatus(trip.status);

            return (
              <Card
                key={trip.id}
                className={cn(
                  'card-base card-interactive',
                  atCustoms && 'border border-flame/50'
                )}
                onClick={() => navigate(`/trips/${trip.id}`)}
                data-testid={`trip-card-${trip.id}`}
              >
                <CardContent className="p-3">
                  {/* Truck matricule */}
                  <div className="matricule-plate text-sm md:text-base mb-2">
                    {nomLisible(trip.tractor_plate, truck?.matricule, 'Camion non précisé')}
                  </div>

                  {/* Route */}
                  {trip.lot_id && !trip.departure_country && !trip.destination_country ? (
                    <div className="mb-1">
                      <PastilleLot lotId={trip.lot_id} reference={referencesLots[trip.lot_id]} />
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-base font-medium mb-1">
                      <span>{texteSur(trip.departure_country, 'Départ non précisé')}</span>
                      <ArrowRight className="w-4 h-4 text-muted-foreground/70" />
                      <span>{texteSur(trip.destination_country, 'Destination non précisée')}</span>
                    </div>
                  )}

                  {/* Departure date */}
                  <p className="text-xs text-muted-foreground mb-2">
                    Départ: {formatDate(trip.departure_date)}
                  </p>

                  {/* Status badges */}
                  <div className="flex flex-wrap gap-1 mb-2">
                    <Badge className="bg-muted text-muted-foreground text-xs">
                      {phaseLabel(getTripPhase(trip.status))}
                    </Badge>
                    {trip.is_blocked && (
                      <Badge className="bg-destructive text-white font-bold animate-pulse text-xs">
                        Bloqué
                      </Badge>
                    )}
                    {enParking(trip) && (
                      <Badge variant="outline" className="text-xs">
                        {libelle('parking_etat', 'oui')}
                      </Badge>
                    )}
                    <Badge
                      className={cn(
                        'status-badge text-xs',
                        getStatusBadgeClass(trip.status)
                      )}
                    >
                      {libelle('tripStatus', trip.status)}
                    </Badge>
                  </div>

                  {/* Progress bar */}
                  <div className="trip-progress">
                    <div
                      className="trip-progress-bar"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-xs text-muted-foreground">
                      {progress}% complété
                    </p>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock className="w-3 h-3" />
                      {formatRelativeTime(trip.updated_at)}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {viewMode !== 'kanban' && trips.length > 0 ? (
        <BarrePagination pagination={pagination} nom="trajets" />
      ) : null}
    </div>
  );
};

export default TripsPage;
